// src/modules/smelter/pages/TransitMemoList.jsx
// Register of every Gold Transit Memo (AGTM) issued — reprint, correct or delete.

import React, { useEffect, useState } from 'react';
import {
  Table, Button, Typography, Row, Col, Input, Space, Popconfirm, Tag, message,
} from 'antd';
import { PlusOutlined, DownloadOutlined, EditOutlined } from '@ant-design/icons';
import { fetchTransitMemos, deleteTransitMemo, downloadTransitMemoPdf } from '../api';
import TransitMemoModal from '../components/TransitMemoModal';
import { usePermissions } from '../../../hooks/usePermissions';

const { Title, Text } = Typography;

export default function TransitMemoList() {
  const { canViewCreateModule, canEditModule, can } = usePermissions();
  const [memos, setMemos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchTransitMemos({ page: pagination.current, limit: pagination.pageSize, search });
      setMemos(res.data || []);
      setPagination((p) => ({ ...p, total: res.pagination?.total || 0 }));
    } catch {
      messageApi.error('Failed to load transit memos');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [pagination.current, pagination.pageSize, search]);

  const handleDownload = async (memo) => {
    setBusyId(memo._id);
    try {
      await downloadTransitMemoPdf(memo._id, memo.memoNo);
    } catch (e) {
      messageApi.error(e.response?.data?.message || 'Failed to download the memo');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (memo) => {
    setBusyId(memo._id);
    try {
      await deleteTransitMemo(memo._id);
      messageApi.success(`Transit memo ${memo.memoNo} deleted`);
      load();
    } catch (e) {
      messageApi.error(e.response?.data?.message || 'Failed to delete the memo');
    } finally {
      setBusyId(null);
    }
  };

  const columns = [
    { title: 'Memo No.', dataIndex: 'memoNo', key: 'memoNo', width: 150,
      render: (v) => <Text strong>{v}</Text> },
    { title: 'Date', dataIndex: 'memoDate', key: 'memoDate', width: 110,
      render: (v) => (v ? new Date(v).toLocaleDateString('en-IN') : '—') },
    { title: 'Destination', key: 'destination', width: 200,
      render: (_, r) => r.destination?.name || '—' },
    { title: 'Carried By', key: 'reps', width: 220,
      render: (_, r) => (r.representatives || []).map((x) => x.name).join(', ') || '—' },
    { title: 'Metal', dataIndex: 'metalType', key: 'metalType', width: 90,
      render: (v) => <Tag color={v === 'SILVER' ? 'default' : 'gold'}>{v || 'GOLD'}</Tag> },
    { title: 'Total Wt (g)', dataIndex: 'totalWeight', key: 'totalWeight', width: 110,
      render: (v) => (Number(v) || 0).toFixed(3) },
    { title: 'Dispatch', dataIndex: 'dispatchNumber', key: 'dispatchNumber', width: 120,
      render: (v) => v || '—' },
    { title: 'Actions', key: 'actions', width: 220, fixed: 'right',
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<DownloadOutlined />} loading={busyId === r._id}
            onClick={() => handleDownload(r)}>
            PDF
          </Button>
          {canEditModule('smelter') && (
            <Button size="small" icon={<EditOutlined />}
              onClick={() => { setEditing(r); setModalOpen(true); }}>
              Edit
            </Button>
          )}
          {can('smelter', 'delete') && (
            <Popconfirm
              title="Delete this transit memo?"
              description="The memo number is not reused."
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={() => handleDelete(r)}
            >
              <Button size="small" danger loading={busyId === r._id}>Delete</Button>
            </Popconfirm>
          )}
        </Space>
      ) },
  ];

  return (
    <div style={{ padding: 24 }}>
      {contextHolder}
      <Row align="middle" justify="space-between" style={{ marginBottom: 16 }} gutter={[12, 12]}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>Gold Transit Memos</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Internal control document carried with the metal while it is in transit to a smelter.
          </Text>
        </Col>
        <Col>
          <Space wrap>
            <Input.Search
              allowClear placeholder="Memo no. / destination / dispatch" style={{ width: 260 }}
              onSearch={(v) => { setSearch(v); setPagination((p) => ({ ...p, current: 1 })); }}
            />
            <Button type="primary" icon={<PlusOutlined />}
              disabled={!canViewCreateModule('smelter')}
              onClick={() => { setEditing(null); setModalOpen(true); }}>
              New Transit Memo
            </Button>
          </Space>
        </Col>
      </Row>

      <Table
        rowKey="_id"
        loading={loading}
        dataSource={memos}
        columns={columns}
        scroll={{ x: 1200 }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          onChange: (current, pageSize) => setPagination((p) => ({ ...p, current, pageSize })),
        }}
      />

      <TransitMemoModal
        open={modalOpen}
        memo={editing}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        onSaved={load}
      />
    </div>
  );
}
